import { Box, Button, ButtonProps, Tooltip } from '@mui/material';
import React, { Fragment, useCallback, useMemo, useState } from 'react';

import { getListItemKey } from '@shared/helpers/utils.helpers';
import {
  ElementSelectPropertyCountType,
  IElementPropertyOption,
} from '@shared/models/element-property.model';
import { mergeSx } from '@client/helpers/mui.helpers';
import { useObjectMemo } from '@client/hooks/use-object.hooks';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { NodonTheme } from '@client/style';
import { Menu, MenuProps } from '@ui/menus/Menu';
import { MenuWidth } from '@ui/menus/Menu/menu.model';
import { DropdownIndicator } from '@ui/DropdownIndicator';
import { Icon } from '@ui/Icon';
import { EllipsisText } from '@ui/EllipsisText/EllipsisText';
import { ConcreteType } from '@shared/templates/categories/concrete/concrete.model';
import { BoardType } from '@shared/templates/categories/gypsum/gypsum.model';
import { InsulationMaterial } from '@shared/templates/categories/insulation/insulation.model';
import { WoodType } from '@shared/templates/categories/wood/wood.model';
import { otherNodonProduct } from '@shared/templates/categories/other-product/other-products.helpers';
import { sbefDividerItems } from '@shared/construction/sbef.model';
import { getSbefPropertyOptions } from '@shared/templates/categories/common-element-category-properties.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';

type PropertyMenuProps = {
  selectedValue: ElementSelectPropertyCountType | undefined;
  selectedLabel?: string;
  options: IElementPropertyOption[];
  isFallback?: boolean;
  onSelect: (value: ElementSelectPropertyCountType) => void;
  buttonProps?: ButtonProps;
  buttonLabelProps?: React.ComponentProps<typeof EllipsisText>;
  tooltip?: string;
  labelPrefix?: string;
  label?: React.ReactNode;
  menuWidth?: MenuWidth | number;
  disabled?: boolean;
} & Omit<MenuProps, 'children' | 'open' | 'width'>;

export const PropertyMenu: React.FC<PropertyMenuProps> = ({
  selectedValue,
  selectedLabel = selectedValue?.toString(),
  options,
  onSelect,
  isFallback = false,
  ...props
}) => {
  const readonly = useIsReadonly();
  const [localAnchor, setLocalAnchor] = useState<Element | undefined>();

  const {
    buttonProps,
    buttonLabelProps,
    tooltip,
    labelPrefix,
    anchorOrigin,
    anchorPosition,
    transformOrigin,
    menuWidth,
    disabled,
    label,
    ...menuProps
  } = props;

  const closeMenu = useCallback(() => {
    setLocalAnchor(undefined);
  }, []);

  const handleSelect = useCallback(
    (value: ElementSelectPropertyCountType) => {
      onSelect(value);
      closeMenu();
    },
    [closeMenu, onSelect],
  );

  const handleOpenMenu = useCallback(
    (event: React.MouseEvent<Element>) => {
      if (readonly || disabled) return;
      setLocalAnchor(event.currentTarget);
    },
    [disabled, readonly],
  );

  const isMenuOpen = Boolean(anchorPosition ?? localAnchor);

  const menuButtonProps = useObjectMemo<ButtonProps>({
    ...buttonProps,
    sx: {
      color: NodonTheme.typography[isFallback ? 'caption' : 'body2'].color,
      maxWidth: 175,
      ...buttonProps?.sx,
    },
  });

  const buttonLabel = label ?? selectedLabel ?? 'Select value';
  const isSearchable = options.length > 25;

  const optionItems = useMemo(() => {
    return options.map((option, index) => {
      const optionLabel = option.label ?? option.value?.toString() ?? '';
      const key = getListItemKey(index, optionLabel);
      const showDivider = shouldRenderDivider(optionLabel);
      const selected = option.value === selectedValue;
      return (
        <Fragment key={key}>
          {showDivider && <Menu.Divider />}
          <Menu.Item
            selected={selected}
            icon={selected ? <Icon category="mui" icon="Check" /> : undefined}
            onClick={() => handleSelect(option.value)}
          >
            {optionLabel}
          </Menu.Item>
        </Fragment>
      );
    });
  }, [handleSelect, options, selectedValue]);

  const isDisabled = readonly || disabled;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Tooltip title={tooltip} disableInteractive>
        <span>
          <Button
            {...menuButtonProps}
            endIcon={<DropdownIndicator open={isMenuOpen} />}
            disabled={isDisabled}
            onClick={handleOpenMenu}
            size="small"
            variant="text"
            color="inherit"
          >
            <EllipsisText
              {...buttonLabelProps}
              sx={mergeSx(
                { fontSize: 'inherit', fontWeight: 'inherit' },
                buttonLabelProps?.sx,
              )}
            >
              {labelPrefix && <span>{labelPrefix} </span>}
              {buttonLabel}
            </EllipsisText>
          </Button>
        </span>
      </Tooltip>

      <Menu
        {...menuProps}
        open={isMenuOpen}
        anchorEl={localAnchor ?? null}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        anchorPosition={anchorPosition}
        onClose={closeMenu}
        width={menuWidth}
        searchable={isSearchable ? 'Search options' : false}
      >
        {optionItems}
      </Menu>
    </Box>
  );
};

const dividerItems: string[] = [
  ConcreteType.Other,
  ConcreteType.BalconyAndStairs,
  ConcreteType.Columns,
  BoardType.CelluloseFibre,
  InsulationMaterial.EPS,
  WoodType.OSB,
];

const SBEF_DIVIDER_LABELS = (() => {
  const sbefLabelOptions = getSbefPropertyOptions();
  return sbefDividerItems
    .map((code) => sbefLabelOptions.find((option) => option.value === code))
    .filter(guards.is.defined)
    .map(({ label: sbefLabel }) => sbefLabel);
})();

const DIVIDER_LABELS = new Set<string>([
  ...dividerItems,
  ...SBEF_DIVIDER_LABELS,
]);

const shouldRenderDivider = (label: string): boolean => {
  if (!label) return false;
  if (otherNodonProduct && label === otherNodonProduct.name) return true;
  return DIVIDER_LABELS.has(label);
};
